import React, { useEffect, useMemo } from 'react';
import { useEffectOnce, usePrevious } from 'react-use';
import { DateTime } from 'luxon';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import type { Map as ImMap } from 'immutable';
import { List } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';
import { makeStyles } from 'tss-react/mui';
import MenuItem from '@mui/material/MenuItem';

import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';

import { transactionsActions, transactionsTypes } from 'app-components/flux/transactions';
import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { accountsActions, accountsSelectors, accountsTypes } from 'app-components/flux/accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  institutionLoginsActions,
  institutionLoginsSelectors,
  institutionLoginsTypes,
} from 'app-components/flux/institution-logins';
import { institutionsSelectors } from 'app-components/flux/institutions';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { loadState, useFetchOnce, useLoadState } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';

import underConstructionImage from 'assets/illustrations/Empty-box-disabled.png';
import StripeLogo from 'assets/logos/stripe.svg';

import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import { businessSelectors } from 'app-components/flux/business';
import AccountCard from 'components/AccountCard';
import DevMenuButton from 'components/DevMenuButton';
import { AddPaymentProviderAction } from 'components/Dialogs/AccountDiscovery/actions';
import Dump from 'components/Dump';
import LoadingView from 'components/LoadingView';
import QTip from 'components/QuickenControls/QTip';
import ZeroStateView from 'components/ZeroStateView';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';
import { getFundingAccounts } from 'data/fundingAccounts/actions';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
import { entitlementsSelectors } from 'app-components/flux/entitlements';

const useStyles = makeStyles()(({ palette, spacing }) => ({
  a2aButton: {
    marginRight: 10,
    backgroundColor: palette.background.paper,
  },
  tryAgainButton: {
    backgroundColor: palette.background.paper,
  },
  underConstruction: {
    display: 'block',
    width: '50%',
    marginTop: spacing(5),
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  loadingView: {
    margin: 'auto',
    height: '100%',
  },
  successOrFailureTab: {
    padding: `${spacing(8)} ${spacing(15)}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
}));

const compareAccountNames = (
  leftAccount: Pick<accountsTypes.Account, 'name'>,
  rightAccount: Pick<accountsTypes.Account, 'name'>,
) => (leftAccount.name || '').localeCompare(rightAccount.name || '', undefined, { numeric: true, sensitivity: 'base' });

const useAccountSettingsLoadingState = () => {
  const profileView = useLoadState(profileSelectors);
  const fundingView = useLoadState(fundingAccountSelectors);
  const disableA2A =
    loadState.tryAgainStates.includes(profileView) ||
    loadState.updateRequiredStates.includes(profileView) ||
    loadState.skeletonStates.includes(profileView);

  const institutionView = useLoadState(institutionsSelectors);
  const institutionLoginsView = useLoadState(institutionLoginsSelectors);
  const accountsView = useLoadState(accountsSelectors);

  const showSkeletonView =
    loadState.skeletonStates.includes(institutionView) ||
    loadState.skeletonStates.includes(accountsView) ||
    loadState.skeletonStates.includes(institutionLoginsView) ||
    loadState.skeletonStates.includes(profileView) ||
    loadState.skeletonStates.includes(fundingView);

  const showTryAgainView =
    loadState.tryAgainStates.includes(accountsView) ||
    loadState.tryAgainStates.includes(institutionLoginsView) ||
    loadState.updateRequiredStates.includes(accountsView) ||
    loadState.updateRequiredStates.includes(institutionLoginsView);

  let viewState = 'READY';
  if (showTryAgainView) {
    viewState = 'FAILED';
  } else if (showSkeletonView) {
    viewState = 'LOADING';
  }

  return { viewState, disableA2A };
};

const StripeEntryPoint = React.memo(function StripeEntryPoint() {
  const dispatch = useAppDispatch();
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const availableBizId = useAppSelector(
    (state) => businessSelectors.getAvailableBusinessesForStripeAccount(state)?.[0]?.id,
  );

  if (!businessEnabled || !availableBizId) {
    return null;
  }

  return (
    <Paper sx={{ padding: '24px 24px 24px 10px', mb: 3 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Stack direction='row'>
          <img alt='stripe-logo' src={StripeLogo} width='122px' height='58px' style={{ alignSelf: 'center' }} />
          <Stack direction='column'>
            <Typography variant='titleMedium' sx={{ fontWeight: 500 }}>
              Get paid faster with Stripe
            </Typography>
            <Typography variant='bodyRegular'> Accept instant online payments.</Typography>
          </Stack>
        </Stack>
        <Button
          variant='outlined'
          color='primary'
          onClick={() => {
            tracker.track(tracker.events.enableStripeLearnMore);
            dispatch(AddPaymentProviderAction(availableBizId));
          }}
          sx={{}}
        >
          Learn more
        </Button>
      </Stack>
    </Paper>
  );
});

const Accounts = () => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const a2aTransferEnabled = useAppSelector(a2aTransfersSelectors.a2aTransferEnabled);
  const institutionLogins: ImMap<string, institutionLoginsTypes.InstitutionLogin> | null = useAppSelector(
    institutionLoginsSelectors.getInstitutionLoginsByName,
  );
  const profile = useAppSelector(profileSelectors.getProfileStore);
  const accountsByInstitutionLoginId = useAppSelector(accountsSelectors.getAccountsByInstitutionLoginId);
  const accountEnabled = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('accountsManagement'),
  );

  const showHiddenAccounts = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('orphanedInstitutionLogins'),
  );
  const hiddenAccounts = showHiddenAccounts
    ? institutionLogins?.filter((fiLogin) => !accountsByInstitutionLoginId.has(fiLogin.id))
    : null;

  // Navigate back to dashboard after Stripe OAuth completes when arriving from the task card.
  // AddPaymentProviderAction has no post-success callback, so we watch stripeCount instead.
  const location = useLocation();
  const navigate = useNavigate();
  const returnToDashboard = (location.state as { returnToDashboard?: boolean } | null)?.returnToDashboard ?? false;
  const stripeCount = useAppSelector((state) => accountsSelectors.getStripeAccounts(state).size);
  const prevStripeCount = usePrevious(stripeCount);

  useEffect(() => {
    if (returnToDashboard && prevStripeCount !== undefined && stripeCount > prevStripeCount) {
      navigate('/');
    }
  }, [returnToDashboard, stripeCount, prevStripeCount, navigate]);

  useEffectOnce(() => {
    dispatch(profileActions.getProfile());
    if (a2aTransferEnabled) {
      dispatch(getFundingAccounts());
    }
    dispatch(institutionLoginsActions.getLoginsWithFiIssues());
  });

  useFetchOnce(accountsActions.getAccounts, accountsSelectors, true);

  const { viewState, disableA2A } = useAccountSettingsLoadingState();

  //  The logic used to determine what dialog to open, email, identity, or transfer
  const kickoffA2AFlow = useKickoffA2AFlow();

  const refreshData = () => {
    dispatch(profileActions.getProfile());
    dispatch(institutionLoginsActions.getInstitutionLogins());
    if (a2aTransferEnabled) {
      dispatch(getFundingAccounts());
    }
    //  dispatch(getInstitution(undefined));
    dispatch(accountsActions.getAccounts());
  };

  const manualAccounts = useMemo(() => {
    let unattachedAccounts = accountsByInstitutionLoginId?.get('0') || List();

    // Need to handle case where client (i.e. Windows) adds all manual accounts to an InstitutionLogin. We recognize
    // this by seeing if the institutionId is '0' and the name is not set. The reason we check for name is so that
    // manual grouped accounts can be supported by adding to an InstitutionLogin with a valid name.
    institutionLogins?.valueSeq().forEach((institutionLogin) => {
      if (institutionLogin.institutionId === '0' && institutionLogin.name === 'unknown') {
        const accounts = accountsByInstitutionLoginId ? accountsByInstitutionLoginId.get(institutionLogin.id) : null;
        if (accounts) {
          unattachedAccounts = unattachedAccounts.concat(accounts);
        }
      }
    });

    const myManualAccounts = unattachedAccounts
      .filter((account) => !accountsSelectors.isGoalAccount(account))
      .sort(compareAccountNames);

    return myManualAccounts.size > 0 ? myManualAccounts : null;
  }, [accountsByInstitutionLoginId, institutionLogins]);

  const handleRequestCreate = () => {
    dispatch(accountDiscoveryActions.doAccountDiscovery({ initiator: 'settings accounts' }));
  };

  const renderCards = useMemo(
    () => (
      <Stack gap={2} width='100%'>
        {accountsByInstitutionLoginId
          .entrySeq()
          .sort(
            (a, b) =>
              (institutionLogins?.get(a[0])?.name ?? '')
                .trimStart()
                .localeCompare((institutionLogins?.get(b[0])?.name ?? '').trimStart()) ?? 0,
          )
          .map(([institutionLoginId, institutionLoginAccounts], index) => {
            const institutionLogin = institutionLogins?.get(institutionLoginId);
            if (
              institutionLoginId === '0' ||
              (institutionLogin?.institutionId === '0' && institutionLogin?.name === 'unknown')
            ) {
              // see above comments about manual accounts grouped into a single InstitutionLogin
              return null;
            }

            // let accounts = accountsByInstitutionLoginId ? accountsByInstitutionLoginId.get(institutionLogin.id) : null;
            let accounts = institutionLoginAccounts;
            if (!accounts || accounts.size === 0) {
              verify(false, `orphaned institution login '${institutionLogin?.name}' has no linked accounts`);
              return null;
            }
            accounts = accounts.sort(compareAccountNames);

            return (
              <AccountCard
                id={`account-card-${index}`}
                accounts={accounts}
                institutionLogin={institutionLogin}
                key={institutionLogin?.id || institutionLogin?.clientId}
              />
            );
          })
          .toList()}

        {manualAccounts && (
          <AccountCard
            id='account-card-manual'
            accounts={manualAccounts}
            key='manual-accounts'
            title='Manual Accounts'
          />
        )}
        {hiddenAccounts?.valueSeq().map((institutionLogin) => (
          <AccountCard
            id={`hidden-account-card-${institutionLogin?.name}`}
            accounts={null}
            institutionLogin={institutionLogin}
            key={`hidden-account-${institutionLogin?.name}-${institutionLogin?.id}`}
            title={institutionLogin?.name}
          />
        ))}
      </Stack>
    ),
    [accountsByInstitutionLoginId, hiddenAccounts, institutionLogins, manualAccounts],
  );

  if (!accountEnabled) {
    return <img className={classes.underConstruction} alt='page is under construction' src={underConstructionImage} />;
  }

  return (
    <>
      {viewState === 'READY' && (
        <Grid container direction='column'>
          <Dump obj={profile as object} id='profile' />

          <SettingsHeader
            title='Accounts'
            description='Manage your financial institutions and accounts'
            primaryItem={
              <>
                <DevMenuButton>
                  <MenuItem
                    onClick={() => {
                      const resources = accountsByInstitutionLoginId
                        .toIndexedSeq()
                        .reduce((allAccounts, accounts) => allAccounts.concat(accounts), List<accountsTypes.Account>());
                      const index = resources.size + 1;
                      const account = accountsTypes.mkAccount('BANK', {
                        balanceAsOf: 0,
                        balanceAsOfOn: '1970-01-01',
                        // that code below would hack around 0 manual account balance racing issue
                        // currentBalanceAsOf: 0,
                        // currentBalanceAsOfOn: DateTime.now().toISODate(),
                        clientId: uuidv4().toUpperCase(),
                        name: String(index),
                        subType: 'CHECKING',
                        type: 'BANK',
                      });
                      new Promise((resolve, reject) =>
                        dispatch(
                          accountsActions.createAccount(account, {
                            resolve,
                            reject,
                            undo: { userMessage: 'account created' },
                          }),
                        ),
                      )
                        .then((response: any) => {
                          const transaction = transactionsTypes.mkCashFlowTransaction({
                            accountId: response.data.id,
                            amount: index,
                            clientId: uuidv4().toUpperCase(),
                            coa: { type: 'BALANCE_ADJUSTMENT', id: '0' },
                            isExcludedFromF2S: true,
                            isExcludedFromReports: true,
                            isReviewed: true,
                            matchState: 'NOT_MATCHED',
                            payee: 'Opening Balance',
                            postedOn: DateTime.now().toISODate(),
                            source: 'USER_ENTERED',
                            state: 'RECONCILED',
                            type: 'CASH_FLOW',
                          });
                          dispatch(
                            transactionsActions.createTransaction(transaction, {
                              undo: { userMessage: 'transaction created' },
                            }),
                          );
                        })
                        .catch((_error) => {});
                    }}
                  >
                    Create 1 Manual Account
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      const resources = accountsByInstitutionLoginId
                        .toIndexedSeq()
                        .reduce((allAccounts, accounts) => allAccounts.concat(accounts), List<accountsTypes.Account>());
                      for (let index = resources.size + 1; index <= 100; index++) {
                        setTimeout(() => {
                          const account = accountsTypes.mkAccount('BANK', {
                            balanceAsOf: 0,
                            balanceAsOfOn: '1970-01-01',
                            // that code below would hack around 0 manual account balance racing issue
                            // currentBalanceAsOf: 0,
                            // currentBalanceAsOfOn: DateTime.now().toISODate(),
                            clientId: uuidv4().toUpperCase(),
                            name: String(index),
                            subType: 'CHECKING',
                            type: 'BANK',
                          });
                          new Promise((resolve, reject) =>
                            dispatch(
                              accountsActions.createAccount(account, {
                                resolve,
                                reject,
                                undo: { userMessage: 'account created' },
                              }),
                            ),
                          )
                            .then((response: any) => {
                              const transaction = transactionsTypes.mkCashFlowTransaction({
                                accountId: response.data.id,
                                amount: index,
                                clientId: uuidv4().toUpperCase(),
                                coa: { type: 'BALANCE_ADJUSTMENT', id: '0' },
                                isExcludedFromF2S: true,
                                isExcludedFromReports: true,
                                isReviewed: true,
                                matchState: 'NOT_MATCHED',
                                payee: 'Opening Balance',
                                postedOn: DateTime.now().toISODate(),
                                source: 'USER_ENTERED',
                                state: 'RECONCILED',
                                type: 'CASH_FLOW',
                              });
                              dispatch(
                                transactionsActions.createTransaction(transaction, {
                                  undo: { userMessage: 'transaction created' },
                                }),
                              );
                            })
                            .catch((_error) => {});
                        }, 100 * index);
                      }
                    }}
                  >
                    Create 100 Manual Accounts
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      dispatchSimpleNotification('delete dispatched');
                      if (!institutionLogins) return;
                      const resources = institutionLogins
                        .toIndexedSeq()
                        .map((institutionLogin) =>
                          institutionLoginsTypes.mkInstitutionLogin({
                            id: institutionLogin.id,
                            isDeleted: true,
                          }),
                        )
                        .toArray();
                      dispatch(institutionLoginsActions.batchInstitutionLogins(resources as any));
                    }}
                  >
                    Delete all logins
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      dispatchSimpleNotification('delete dispatched');
                      const resources = accountsByInstitutionLoginId
                        .toIndexedSeq()
                        .reduce((allAccounts, accounts) => allAccounts.concat(accounts), List<accountsTypes.Account>())
                        .map((account) =>
                          accountsTypes.mkAccount(account.type, {
                            clientId: account.clientId,
                            id: account.id,
                            isDeleted: true,
                          }),
                        )
                        .toArray();
                      dispatch(accountsActions.batchAccounts(resources));
                    }}
                  >
                    Delete all accounts
                  </MenuItem>
                </DevMenuButton>
                <Button
                  startIcon={<AddCircleRoundedIcon />}
                  id='accounts-add-new-button'
                  variant='contained'
                  onClick={handleRequestCreate}
                >
                  Account
                </Button>
              </>
            }
            secondaryItem={
              a2aTransferEnabled && (
                <QTip title={disableA2A ? 'Error fetching data, please refresh the app' : ''}>
                  <Button
                    disabled={disableA2A}
                    onClick={() =>
                      kickoffA2AFlow({
                        initiator: 'settings page',
                      })
                    }
                    variant='outlined'
                    className={classes.a2aButton}
                    startIcon={<SwapHorizRoundedIcon />}
                  >
                    {'Set Up Transfers'}
                  </Button>
                </QTip>
              )
            }
          />
          <Grid container direction='column'>
            <StripeEntryPoint />
            {renderCards}
          </Grid>
        </Grid>
      )}
      {viewState === 'LOADING' && <LoadingView size={100} className={classes.loadingView} />}
      {viewState === 'FAILED' && (
        <ZeroStateView
          className={classes.successOrFailureTab}
          button={
            <Button id={'try-again-button'} className={classes.tryAgainButton} variant='outlined' onClick={refreshData}>
              TRY AGAIN
            </Button>
          }
          id='failed-to-fetch'
          title='Unable to fetch data'
          description="We can't fetch your data from the server. Please try again"
        />
      )}
    </>
  );
};

export default Accounts;
